import React, { useState, useEffect } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, FlatList, TextInput,
  KeyboardAvoidingView, Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useToast } from 'react-native-toast-notifications';
import { useGame } from '../../context/GameContext';
import { SCREEN_NAMES } from '../../utils/constant';
import { theme } from '../Common.style';

export default function GameCluePhaseScreen() {
  const navigation = useNavigation();
  const toast = useToast();
  const {
    room, currentRound, isMyClutTurn, submitClue,
    onlinePlayers, amImpostor, mySecretWord, localPlayers, submitLocalClue,
  } = useGame();

  const [clueInput, setClueInput] = useState('');
  const [loading, setLoading] = useState(false);

  const isLocal = room?.mode === 'LOCAL';
  const clues = currentRound?.clues ?? [];
  const players = isLocal ? localPlayers : onlinePlayers;
  const totalPlayers = players.length;
  const currentPlayerIdx = totalPlayers > 0 ? clues.length % totalPlayers : 0;
  const currentPlayer = players[currentPlayerIdx];
  const isMyTurn = isLocal ? true : isMyClutTurn;

  useEffect(() => {
    if (!room || !currentRound) {
      navigation.replace(SCREEN_NAMES.GAME_LOBBY);
    } else if (currentRound.status === 'DISCUSSION' || currentRound.status === 'VOTING'
      || currentRound.status === 'IMPOSTOR_GUESS' || currentRound.status === 'FINISHED') {
      navigation.navigate(SCREEN_NAMES.GAME_DISCUSSION);
    }
  }, [room?.id, currentRound?.status]);

  if (!room || !currentRound) return null;
  if (currentRound.status !== 'CLUE_PHASE') return null;

  const handleSubmit = async () => {
    const word = clueInput.trim();
    if (!word) return;
    if (word.includes(' ')) {
      toast.show('Un seul mot autorisé', { type: 'warning' });
      return;
    }

    try {
      setLoading(true);
      if (isLocal) {
        submitLocalClue(currentPlayerIdx, word);
        setClueInput('');
        if (clues.length + 1 >= totalPlayers) {
          navigation.navigate(SCREEN_NAMES.GAME_DISCUSSION);
        }
      } else {
        await submitClue(word);
        setClueInput('');
        if (clues.length + 1 >= totalPlayers) {
          navigation.navigate(SCREEN_NAMES.GAME_DISCUSSION);
        }
      }
    } catch (e) {
      toast.show(e.message || 'Erreur', { type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const localCurrentName = isLocal
    ? (localPlayers[currentPlayerIdx]?.name ?? `Joueur ${currentPlayerIdx + 1}`)
    : null;

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <View style={styles.container}>
        <Text style={styles.title}>Phase des indices</Text>
        <Text style={styles.progress}>{clues.length}/{totalPlayers} indices donnés</Text>

        {/* Clue list */}
        <FlatList
          data={players}
          keyExtractor={(_, i) => String(i)}
          style={styles.list}
          renderItem={({ item, index }) => {
            const clue = clues.find((c) => c.order === index);
            const name = isLocal ? (item.name ?? `J${index + 1}`) : (item.user?.firstName ?? 'Joueur');
            const isActive = index === currentPlayerIdx;
            return (
              <View style={[styles.clueRow, isActive && styles.clueRowActive]}>
                <View style={[styles.playerDot, isActive && styles.playerDotActive]}>
                  <Text style={styles.playerDotText}>{name[0]?.toUpperCase()}</Text>
                </View>
                <Text style={[styles.playerLabel, isActive && styles.playerLabelActive]}>{name}</Text>
                {clue ? (
                  <Text style={styles.clueWord}>{clue.word}</Text>
                ) : isActive ? (
                  <Text style={styles.cluePending}>…</Text>
                ) : (
                  <Text style={styles.clueEmpty}>En attente</Text>
                )}
              </View>
            );
          }}
        />

        {/* Input bar */}
        {isMyTurn && (
          <View style={styles.inputBar}>
            {isLocal && (
              <Text style={styles.turnLabel}>
                {localCurrentName}, c'est ton tour !
              </Text>
            )}
            {(!amImpostor || isLocal) && (
              <Text style={styles.wordHint}>
                Mot secret : {isLocal
                  ? (currentPlayerIdx === currentRound.impostorId ? '???' : currentRound.secretWord)
                  : (mySecretWord ?? '???')}
              </Text>
            )}
            <View style={styles.inputRow}>
              <TextInput
                style={styles.input}
                placeholder="Un seul mot…"
                value={clueInput}
                onChangeText={setClueInput}
                autoCapitalize="none"
                returnKeyType="send"
                onSubmitEditing={handleSubmit}
              />
              <TouchableOpacity
                style={[styles.sendBtn, (!clueInput.trim() || loading) && styles.btnDisabled]}
                onPress={handleSubmit}
                disabled={!clueInput.trim() || loading}
              >
                <Text style={styles.sendBtnText}>Valider</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {!isMyTurn && !isLocal && (
          <View style={styles.waitBar}>
            <Text style={styles.waitText}>
              En attente de {currentPlayer?.user?.firstName ?? 'joueur'}…
            </Text>
          </View>
        )}
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1, backgroundColor: '#fff' },
  container: { flex: 1, backgroundColor: '#fff' },
  title: { fontSize: 22, fontWeight: '800', textAlign: 'center', marginTop: 16, marginBottom: 4 },
  progress: { textAlign: 'center', color: theme.colors.primary, fontWeight: '600', marginBottom: 12 },
  list: { flex: 1, paddingHorizontal: 16 },
  clueRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  clueRowActive: { backgroundColor: theme.colors.primary + '10', borderRadius: 12, paddingHorizontal: 8 },
  playerDot: { width: 36, height: 36, borderRadius: 18, backgroundColor: '#ddd', alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  playerDotActive: { backgroundColor: theme.colors.primary },
  playerDotText: { color: '#fff', fontWeight: '700' },
  playerLabel: { flex: 1, fontSize: 15, color: '#444' },
  playerLabelActive: { color: theme.colors.primary, fontWeight: '700' },
  clueWord: { fontSize: 18, fontWeight: '800', color: '#222' },
  cluePending: { fontSize: 22, color: theme.colors.primary },
  clueEmpty: { fontSize: 13, color: '#bbb' },
  inputBar: { borderTopWidth: 1, borderTopColor: '#eee', padding: 16, backgroundColor: '#fff' },
  turnLabel: { fontWeight: '700', fontSize: 15, marginBottom: 4, color: '#333' },
  wordHint: { color: theme.colors.primary, fontWeight: '600', marginBottom: 8 },
  inputRow: { flexDirection: 'row', gap: 8 },
  input: { flex: 1, borderWidth: 1.5, borderColor: '#ddd', borderRadius: 12, paddingHorizontal: 14, paddingVertical: 10, fontSize: 16 },
  sendBtn: { backgroundColor: theme.colors.primary, borderRadius: 12, paddingHorizontal: 20, alignItems: 'center', justifyContent: 'center' },
  btnDisabled: { opacity: 0.4 },
  sendBtnText: { color: '#fff', fontWeight: '700' },
  waitBar: { borderTopWidth: 1, borderTopColor: '#eee', padding: 16, alignItems: 'center' },
  waitText: { color: '#888', fontSize: 15 },
});
